/* stylelint-disable CssSyntaxError */
import { type SDK_Grid, colors, strings } from "@quickpoint.me/shared";
import {
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useLayoutEffect,
	useRef,
} from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import type { ElementsProps } from "../../@types/viewerati";
import type { GridObject } from "../../lib/calculateGridRowsAndColumns";
import { TUFTE_VERTICAL_PAD_FRACTION } from "../../lib/gridTypes";
import { mainCssRules } from "../../lib/cssTemplates";
import { CurrentFilter, FilterFonts } from "../../state/filter";
import { PageIndex, PageSizeAtom } from "../../state/page";
import { AllLoaded } from "../../state/selectors";
import {
	AutoSpaceAtom,
	AutoSpaceFilterSetting,
	ConstrainDimensionsSelector,
	EditMode,
	IsPreviewGenerator,
} from "../../state/ui";
import { fontSize } from "../../lib/cssHelpers";
import Cell from "./Cell";
import { GridContext } from "./DisplayElements";
import TableCell from "./TableCell";

// WebKit (Safari and all iOS browsers): AppleWebKit UA without the Chrome/Chromium
// token that Blink-based browsers carry.
const isWebKitBrowser =
	/AppleWebKit/.test(navigator.userAgent) &&
	!/Chrome\/|Chromium\//.test(navigator.userAgent);

export function getStartingPoint(value: SDK_Grid["startingPoint"]) {
	switch (value) {
		case "middleOut":
			return "center";
		case "topDown":
			return "start";
		case "bottomUp":
			return "end";
		default:
			return "center";
	}
}

interface RenderGridProps extends Omit<ElementsProps, "$stickyHeaderHeight"> {
	$alignCellsAsColumn: boolean;
	$baseFontSizeRaw: number;
	$forceSingleColumn: boolean;
	$maxCols: number;
	$maxRows: number;
	$inEditMode: boolean;
	$isSplitPageChild?: boolean;
	$gridContainsImages: boolean;
	$isWebkit: boolean;
	$isResponsiveTable?: boolean;
	$verticalPad: number;
	$autoRowGap?: string;
}

export const StyledGrid = styled.div<RenderGridProps>`
  display: grid;
  justify-items: ${(props) => props.theme.grid.align};
  grid-template-rows: ${(props) =>
		props.$isSplitPageChild && !props.$isInLandscape
			? `repeat(${props.$maxRows}, 1fr)`
			: "auto"};;
  grid-template-columns: ${(props) => {
		// WCAG 1.4.10: Force single column when font is enlarged
		const useMultiColumn = props.$isInLandscape && !props.$forceSingleColumn;
		if (!useMultiColumn) return props.$isResponsiveTable ? "auto" : "1fr";
		return `repeat(${props.$maxCols}, 1fr)`;
	}};
  column-gap: ${(props) => (props.theme.grid.columnGap ? `${props.theme.grid.columnGap * props.$baseFontSize}px` : "unset")};
  /* autospace-derived rhythm when the toggle is on; theme gap otherwise */
  row-gap: ${(props) => {
		if (!props.$isInLandscape) return "unset";
		if (props.$autoRowGap) return props.$autoRowGap;
		return props.theme.grid.rowGap ? `${props.theme.grid.rowGap * props.$baseFontSize}px` : "unset";
	}};
  // after days of messing a decision. The user must fill the grid. No adding height on mobile
  // min-height: 100% is nice on mobile but it messes the layouts..
  ${(props) => (props.$isInLandscape && !props.$forceSingleColumn ? "max-height: 100%;" : "height: 100%;")}
  position: relative;
  z-index: 0;
  ${(props) =>
		`${mainCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			theme: props.theme.grid,
		})}`}
  align-items: ${(props) => props.$forceSingleColumn ? "start" : getStartingPoint(props.theme.grid.startingPoint)};
  max-width: 100%;
  max-height: ${(props) => (props.$isInLandscape && !props.$forceSingleColumn ? "100%" : "unset")};
  height: ${(props) => (props.$isInLandscape && !props.$forceSingleColumn ? "100%" : "auto")};
  width: 100%;
  ${(props) => (props.$isInLandscape && !props.$forceSingleColumn ? "min-height: 0" : "")};
  .overlay {
    position: absolute;
    display: grid;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    grid-gap: inherit;
    pointer-events: none;
  }
  .overlay>* {
    background-size: cover;
    opacity: 0.1;
    border:  ${(props) => (props.$inEditMode ? `2px solid ${colors.yellow}` : "unset")};
  }
   ${(props) => (props.$isInLandscape ? "" : "padding-inline: 0;")}
  /* weighted grids get vertical breathing room to match the stacked-page rhythm */
  ${(props) => (props.$verticalPad ? `padding-block: ${props.$verticalPad}px;` : "")}
 `;

/**
 * @param gridArray : A 2 or 3d array representing a grid with one row or many rows
 *
 * @returns array of JSX Elements with the correct Grid CSS applied.
 */
function RenderGrid({
	gridArray,
	alignCellsAsColumn,
	setGridHasRenderedCallback,
	isSplitPageChild,
}: {
	gridArray: GridObject[];
	alignCellsAsColumn: boolean;
	setGridHasRenderedCallback?: Dispatch<SetStateAction<boolean>>;
	isSplitPageChild?: boolean;
}) {
	// console.log("RenderGrid received gridArray:", JSON.stringify(gridArray, null, 2));
	const pageSize = useRecoilValue(PageSizeAtom);
	const inEditMode = useRecoilValue(EditMode);
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const baseFontSize = pageSize.baseFontSize;
	const pageIndex = useRecoilValue(PageIndex);
	const filter = useRecoilValue(CurrentFilter);
	const fonts = useRecoilValue(FilterFonts);
	const isResponsiveTable =
		gridArray.length > 0 && gridArray[0].isResponsiveTable;
	const className = `${strings.css.grid} ${isSplitPageChild ? strings.css.splitPage : ""} ${isResponsiveTable ? "responsive-table-grid" : ""}`;
	const allLoaded = useRecoilValue(AllLoaded); // useful for measuring images
	const userAutoSpace = useRecoilValue(AutoSpaceAtom);
	const filterAutoSpace = useRecoilValue(AutoSpaceFilterSetting);
	const blankGap = fontSize(filter.blank, baseFontSize, filter.blank.leading);
	const autoRowGap =
		userAutoSpace && filterAutoSpace && blankGap !== strings.unset
			? blankGap
			: undefined;
	const gridContainsImages = gridArray.some((el) =>
		el.elArray.some((element) => element.props.type === "image:"),
	);
	const isWebKit = isWebKitBrowser;
	const renderCell = (gridObject: GridObject, idx: number) => {
		const CellComponent = isResponsiveTable ? TableCell : Cell;
		return (
			<CellComponent
				gridObject={gridObject}
				idx={idx}
				isPreviewer={isPreviewer}
				inEditMode={inEditMode}
				isSplitPageChildCell={isSplitPageChild}
				alignCellAsColumn={alignCellsAsColumn}
				key={`cell-${idx}-${pageIndex}`}
			/>
		);
	};

	/* responsive tables need role=row wrappers between table and cells or
	   assistive tech can't associate headers with cells. display:contents
	   keeps the cells as direct CSS-grid items so layout is untouched */
	let cells: ReactNode;
	if (isResponsiveTable) {
		const rowGroups = new Map<number, ReactNode[]>();
		gridArray.forEach((gridObject, idx) => {
			const group = rowGroups.get(gridObject.row) ?? [];
			group.push(renderCell(gridObject, idx));
			rowGroups.set(gridObject.row, group);
		});
		cells = [...rowGroups.entries()].map(([rowNumber, rowCells]) => (
			<div
				role="row"
				style={{ display: "contents" }}
				key={`table-row-${rowNumber}-${pageIndex}`}
			>
				{rowCells}
			</div>
		));
	} else {
		cells = gridArray.map(renderCell);
	}

	const gridRef = useRef<HTMLDivElement>(null);

	let isInLandscape = useRecoilValue(PageSizeAtom).landscape;
	isInLandscape = !filter.grid.singleColumnInPortrait ? true : isInLandscape;

	useLayoutEffect(() => {
		if (setGridHasRenderedCallback) {
			setGridHasRenderedCallback(true);
		}
		gridArray;
		allLoaded;
	}, [gridArray, setGridHasRenderedCallback, allLoaded]);

	if (gridArray.length === 0) {
		return <></>;
	}

	const grid = (
		<StyledGrid
			/* layout grids get no ARIA role — role="grid" is an interactive
			   widget role and misleads assistive tech on plain layouts */
			role={isResponsiveTable ? "table" : undefined}
			$alignCellsAsColumn={false}
			$baseFontSize={baseFontSize}
			$baseFontSizeRaw={pageSize.baseFontSizeRaw}
			$fonts={fonts}
			$forceSingleColumn={pageSize.forceSingleColumn}
			$gridContainsImages={gridContainsImages}
			$inEditMode={inEditMode}
			$constrainDimensions={constrainDimensions}
			$isInLandscape={isInLandscape}
			$isPreviewer={isPreviewer}
			$isSplitPageChild={isSplitPageChild}
			$isWebkit={isWebKit}
			$isResponsiveTable={isResponsiveTable}
			$maxCols={gridArray[0].maxCols}
			$maxRows={gridArray[0].maxRows}
			$verticalPad={gridArray[0].hasWeightedRows && isInLandscape ? pageSize.height * TUFTE_VERTICAL_PAD_FRACTION : 0}
			$autoRowGap={autoRowGap}
			className={className}
			key={`styledGrid-${pageIndex}`}
			ref={gridRef}
		>
			<GridContext.Provider value={true}>{cells}</GridContext.Provider>
		</StyledGrid>
	);
	return grid;
}

export default RenderGrid;
